import { API_ROUTES } from '../shared/api-contract';
import { APP_ORIGIN } from '../shared/site-origins';
import './styles.css';

document.documentElement.classList.add('wa-theme-active', 'wa-dark', 'js');

const APP_URL = APP_ORIGIN;
/**
 * Marketing is served on the apex host; call Netlify Functions on the app host
 * in production. Local Vite proxies relative `/.netlify/functions` to :8888.
 */
const FUNCTIONS_ORIGIN = import.meta.env.DEV ? '' : APP_ORIGIN;

const form = document.getElementById('signup-form') as HTMLFormElement | null;
const statusEl = document.getElementById('status') as HTMLParagraphElement | null;
const submitBtn = document.getElementById('submit-btn') as (HTMLElement & {
  disabled?: boolean;
  loading?: boolean;
}) | null;

function setStatus(message: string, kind: 'error' | 'ok' | '' = ''): void {
  if (!statusEl) return;
  statusEl.hidden = !message;
  statusEl.textContent = message;
  statusEl.classList.toggle('error', kind === 'error');
  statusEl.classList.toggle('ok', kind === 'ok');
}

function setSubmitting(busy: boolean): void {
  if (!submitBtn) return;
  submitBtn.disabled = busy;
  submitBtn.loading = busy;
}

function messageFromError(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message;
  }
  return 'Something went wrong';
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const data = await response.json() as { error?: string; errorMessage?: string };
    if (typeof data.error === 'string' && data.error) return data.error;
    if (typeof data.errorMessage === 'string' && data.errorMessage) return data.errorMessage;
  } catch {
    // Ignore parse errors.
  }
  return `Request failed (${response.status})`;
}

function inputValue(id: string): string {
  const el = document.getElementById(id) as (HTMLElement & { value?: string }) | null;
  return typeof el?.value === 'string' ? el.value : '';
}

function apiUrl(route: string): string {
  return `${FUNCTIONS_ORIGIN}${route}`;
}

async function completeCheckout(sessionId: string): Promise<void> {
  setStatus('Payment received. Creating your account…');
  setSubmitting(true);

  const response = await fetch(apiUrl(API_ROUTES.completeSignup), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId }),
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }

  setStatus('Account ready. Opening the app…', 'ok');
  window.location.assign(APP_URL);
}

async function bootstrapReturn(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const checkout = params.get('checkout');
  const sessionId = params.get('session_id');

  if (checkout === 'cancel') {
    setStatus('Checkout canceled. You can try again when you are ready.', 'error');
    return;
  }

  if (checkout === 'success' && sessionId) {
    try {
      await completeCheckout(sessionId);
    } catch (err) {
      setStatus(messageFromError(err), 'error');
      setSubmitting(false);
    }
  }
}

form?.addEventListener('submit', (event) => {
  event.preventDefault();
  void (async () => {
    const email = inputValue('email').trim();
    const password = inputValue('password');
    const confirm = inputValue('password-confirm');

    if (password !== confirm) {
      setStatus('Passwords do not match', 'error');
      return;
    }
    if (password.length < 8) {
      setStatus('Password must be at least 8 characters', 'error');
      return;
    }

    setSubmitting(true);
    setStatus('Starting Stripe Checkout…');

    try {
      const response = await fetch(apiUrl(API_ROUTES.startSignupCheckout), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }
      const data = await response.json() as { url?: string };
      if (!data.url) {
        throw new Error('Checkout did not return a URL');
      }
      window.location.assign(data.url);
    } catch (err) {
      setStatus(messageFromError(err), 'error');
      setSubmitting(false);
    }
  })();
});

void bootstrapReturn();
